/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";

export enum EMessageTemplateType {
  INFO_ACCOUNT = "INFO_ACCOUNT",
  UPDATE_CRED = "UPDATE_CRED",
  TRANSLATE_ACCOUNT = "TRANSLATE_ACCOUNT",
  PAYMENT_REMINDER = "PAYMENT_REMINDER",
  PAYMENT = "PAYMENT",
}

export const MESSAGE_TEMPLATE_TYPE_LABEL: Record<EMessageTemplateType, string> = {
  [EMessageTemplateType.INFO_ACCOUNT]: "Información de cuenta",
  [EMessageTemplateType.UPDATE_CRED]: "Actualización de credenciales",
  [EMessageTemplateType.TRANSLATE_ACCOUNT]: "Traspaso de cuenta",
  [EMessageTemplateType.PAYMENT_REMINDER]: "Recordatorio de pago",
  [EMessageTemplateType.PAYMENT]: "Pago",
};

export interface MessageTemplate {
  id: number;
  name: string;
  type: EMessageTemplateType;
  body: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
}

export interface CreateMessageTemplateRequest {
  name: string;
  type: EMessageTemplateType;
  body: string;
  variables: string[];
  isActive?: boolean;
}

export interface UpdateMessageTemplateRequest {
  name?: string;
  type?: EMessageTemplateType;
  body?: string;
  variables?: string[];
  isActive?: boolean;
}

export interface MessageTemplateFilters {
  type?: EMessageTemplateType;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface SendMessageTemplateRequest {
  streamingAccountId?: number;
  subscriptionId?: number;
}

function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

export const messageTemplateService = {
  async create(
    data: CreateMessageTemplateRequest,
  ): Promise<IresponseData<MessageTemplate>> {
    try {
      const response = await apiClient.post<IresponseData<MessageTemplate>>(
        "/templates",
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al crear la plantilla. Por favor, intenta de nuevo.",
      );
    }
  },

  async getAll(
    filters?: MessageTemplateFilters,
  ): Promise<IresponseDataPaginated<MessageTemplate[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<
        IresponseDataPaginated<MessageTemplate[]>
      >("/templates", { params: cleanFilters });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener las plantillas. Por favor, intenta de nuevo.",
      );
    }
  },

  async getOne(id: number): Promise<IresponseData<MessageTemplate>> {
    try {
      const response = await apiClient.get<IresponseData<MessageTemplate>>(
        `/templates/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener la plantilla. Por favor, intenta de nuevo.",
      );
    }
  },

  async update(
    id: number,
    data: UpdateMessageTemplateRequest,
  ): Promise<IresponseData<MessageTemplate>> {
    try {
      const response = await apiClient.put<IresponseData<MessageTemplate>>(
        `/templates/${id}`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar la plantilla. Por favor, intenta de nuevo.",
      );
    }
  },

  async remove(id: number): Promise<IresponseData<null>> {
    try {
      const response = await apiClient.delete<IresponseData<null>>(
        `/templates/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al eliminar la plantilla. Por favor, intenta de nuevo.",
      );
    }
  },

  async send(
    id: number,
    data: SendMessageTemplateRequest,
  ): Promise<IresponseData<null>> {
    try {
      const response = await apiClient.post<IresponseData<null>>(
        `/templates/${id}/send`,
        { data },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al enviar el mensaje. Por favor, intenta de nuevo.",
      );
    }
  },
};
